import { useTu } from "@src/appV2/i18n/useTu";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { Box, IconButton as MuiIconButton, InputBase, Stack, Tooltip } from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCallback, useRef, useState } from "react";
import { Button } from "../components/Button";
import { MAX_FILE_SIZE, SUPPORTED_FILE_TYPES } from "./constants";

export interface ChatInputProps {
  onSend: (message: string) => void;
  onFileSelect?: (file: File) => void;
  onInviteToShift?: () => void;
  /** Label for the invite button. Defaults to "Invite to shift". */
  inviteLabel?: string;
  onShareCredentials?: () => void;
  disabled?: boolean;
  /**
   * Initial text seeded into the input. Used when the caller remounts the
   * input via `key` to enter edit mode — combined with the remount, this
   * makes prefill automatic and cancel wipes in-progress text.
   */
  initialValue?: string;
}

/**
 * Chat input with text field, invite-to-shift button, share-credentials key icon,
 * file upload, and send button.
 * Layout matches Figma Workplace Design System, node 20:682.
 */
export function ChatInput(props: ChatInputProps) {
  const { tu } = useTu("chat");

  const {
    onSend,
    onFileSelect,
    onInviteToShift,
    onShareCredentials,
    disabled = false,
    initialValue = "",
  } = props;
  const inviteLabel = props.inviteLabel ?? tu("Invite to shift");
  const [message, setMessage] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    if (message.trim()) {
      onSend(message.trim());
      setMessage("");
    }
  }, [message, onSend]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        showErrorToast(tu("File size must be less than 10MB"));
        return;
      }

      if (!SUPPORTED_FILE_TYPES.includes(file.type as (typeof SUPPORTED_FILE_TYPES)[number])) {
        showErrorToast(tu("Unsupported file type. Please upload images, PDFs, or Word documents."));
        return;
      }

      onFileSelect?.(file);

      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [onFileSelect, tu]
  );

  const hasMessage = Boolean(message.trim());

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: 4,
        borderRadius: theme.borderRadius?.xLarge,
        // State-based styling per Figma (Workplace Design System, node 20:682).
        // Placeholder and Input states share the same treatment, so we only
        // distinguish disabled, focused (typing), and default (placeholder/input).
        // Keep a 1px border in every state (transparent when disabled) to avoid
        // layout shift between states.
        backgroundColor: disabled
          ? theme.palette.background?.inactive
          : isFocused
            ? theme.palette.background?.paper
            : theme.palette.background?.base,
        border: "1px solid",
        borderColor: disabled
          ? "transparent"
          : isFocused
            ? theme.palette.base?.strong
            : theme.palette.border?.base,
      })}
    >
      <input
        ref={fileInputRef}
        aria-label={tu("Attach file")}
        type="file"
        accept={SUPPORTED_FILE_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <InputBase
        fullWidth
        multiline
        maxRows={6}
        placeholder={tu("Type your message...")}
        value={message}
        disabled={disabled}
        autoComplete="off"
        spellCheck="true"
        inputProps={{
          "aria-label": tu("Chat message"),
          maxLength: 5000,
        }}
        sx={(theme) => ({
          ...theme.typography.body2,
          // iOS auto-zooms inputs rendered below 16px on focus; keep touch devices at 16px.
          "@media (pointer: coarse)": {
            fontSize: "16px",
          },
          "& .MuiInputBase-input::placeholder": {
            color: theme.palette.base?.inactive,
            opacity: 1,
          },
        })}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
      />
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        {onInviteToShift && (
          <Button
            aria-label={inviteLabel}
            disabled={disabled}
            size="small"
            startIconType="plus"
            variant="outlined"
            onClick={onInviteToShift}
          >
            {inviteLabel}
          </Button>
        )}

        <Stack direction="row" sx={{ alignItems: "center", gap: 2, marginLeft: "auto" }}>
          {onShareCredentials && (
            <Tooltip title={tu("Share credentials")} placement="top">
              <span>
                <MuiIconButton
                  aria-label={tu("Share credentials")}
                  disabled={disabled}
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                    padding: "5px",
                    "&:hover": {
                      color: theme.palette.text.primary,
                    },
                    "&.Mui-disabled": {
                      color: theme.palette.base?.inactive,
                    },
                  })}
                  onClick={onShareCredentials}
                >
                  <VpnKeyIcon sx={{ fontSize: "14px" }} />
                </MuiIconButton>
              </span>
            </Tooltip>
          )}
          {onFileSelect && (
            <MuiIconButton
              aria-label={tu("Attach file")}
              disabled={disabled}
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                padding: "5px",
                "&:hover": {
                  color: theme.palette.text.primary,
                },
                "&.Mui-disabled": {
                  color: theme.palette.base?.inactive,
                },
              })}
              onClick={handleFileClick}
            >
              <AttachFileIcon sx={{ fontSize: "14px" }} />
            </MuiIconButton>
          )}
          <MuiIconButton
            aria-label={tu("Send message")}
            disabled={disabled || !hasMessage}
            sx={(theme) => ({
              backgroundColor: hasMessage && !disabled ? theme.palette.base?.base : "transparent",
              color:
                hasMessage && !disabled
                  ? theme.palette.base?.inverted?.strong
                  : theme.palette.base?.inactive,
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              "&:hover": {
                backgroundColor:
                  hasMessage && !disabled ? theme.palette.base?.strong : "transparent",
              },
              "&.Mui-disabled": {
                backgroundColor: "transparent",
                color: theme.palette.base?.inactive,
              },
            })}
            onClick={handleSend}
          >
            <SendIcon sx={{ fontSize: "18px" }} />
          </MuiIconButton>
        </Stack>
      </Stack>
    </Box>
  );
}
